import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect, Suspense, lazy } from 'react'
import { Toaster } from 'sonner'
import { PageviewTracker } from '@/components/providers/PageviewTracker'
import { LoginPage } from '@/pages/LoginPage'
import { FeedPage } from '@/pages/FeedPage'
import { PortfolioPage } from '@/pages/PortfolioPage'
import { SmsConsentPage } from '@/pages/SmsConsentPage'
import { SandboxPage } from '@/pages/SandboxPage'
import { authRepo } from '@/lib/repositories'

// Public marketing & legal pages — lazy-loaded so they don't bloat the authenticated bundle
const LandingPage = lazy(() =>
  import('@/pages/landing/LandingPage').then((m) => ({ default: m.LandingPage })),
)
const TermsPage = lazy(() =>
  import('@/pages/landing/TermsPage').then((m) => ({ default: m.TermsPage })),
)
const PrivacyPage = lazy(() =>
  import('@/pages/landing/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
)

// Shared loading component
function PageLoader() {
  return (
    <div className="min-h-screen bg-sovereign-mesh flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center animate-pulse">
          <span
            className="text-black text-3xl"
            style={{
              fontFamily: "'garamond-atf-text', 'EB Garamond', serif",
              fontStyle: 'italic',
              WebkitTextStroke: '1px black',
            }}
          >
            S
          </span>
        </div>
        <div className="text-neutral-400 text-sm">Loading...</div>
      </div>
    </div>
  )
}

/**
 * ProtectedRoute - Redirects to /login if user is not authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    authRepo.isAuthenticated().then(setIsAuthenticated)
  }, [])

  // Loading state
  if (isAuthenticated === null) {
    return <PageLoader />
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

/**
 * RootRoute - Public landing page for unauthenticated users,
 * redirect to /feed for authenticated users.
 */
function RootRoute() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    authRepo.isAuthenticated().then(setIsAuthenticated)
  }, [])

  if (isAuthenticated === null) {
    return <PageLoader />
  }

  if (isAuthenticated) {
    return <Navigate to="/feed" replace />
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <LandingPage />
    </Suspense>
  )
}

/**
 * App - Main router setup with hybrid routing strategy
 * Routes:
 *   /login - LoginPage (unauthenticated)
 *   /feed  - FeedPage (protected)
 *   /      - Redirect to /feed
 */
function App() {
  return (
    <BrowserRouter>
      <PageviewTracker />

      {/* Toast notifications with dismiss options */}
      <Toaster
        position="top-center"
        closeButton={true}
        richColors={false}
        duration={4000}
        theme="dark"
        toastOptions={{
          style: {
            background: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            color: '#fafafa',
            fontFamily:
              "'BDO Grotesk', 'Space Grotesk', 'Be Vietnam Pro', system-ui, sans-serif",
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(12px)',
          },
          classNames: {
            toast: 'group',
            title: 'text-white font-medium',
            description: 'text-neutral-400 text-sm',
            closeButton:
              'bg-neutral-800 border-neutral-700 hover:bg-neutral-700 text-neutral-400 hover:text-white',
            success: 'border-emerald-500/30 bg-emerald-500/10',
            error: 'border-red-500/30 bg-red-500/10',
            warning: 'border-amber-500/30 bg-amber-500/10',
            info: 'border-cyan-500/30 bg-cyan-500/10',
          },
        }}
      />

      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sms-consent" element={<SmsConsentPage />} />

        {/* Protected routes */}
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <FeedPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/portfolio"
          element={
            <ProtectedRoute>
              <PortfolioPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/sandbox"
          element={
            <ProtectedRoute>
              <SandboxPage />
            </ProtectedRoute>
          }
        />

        {/* Public landing for unauthenticated users; redirect to /feed if logged in */}
        <Route path="/" element={<RootRoute />} />

        {/* Public legal pages — accessible to anyone, any auth state */}
        <Route
          path="/terms"
          element={
            <Suspense fallback={<PageLoader />}>
              <TermsPage />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<PageLoader />}>
              <PrivacyPage />
            </Suspense>
          }
        />

        {/* 404 - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
